// What a Patient Quick Note editor is opened against (NO-849): an Appointment
// (the appointment workspace, NO-445) or the Client itself (the patient page,
// on NO-847's Client-keyed routes). Both read and write the same note with
// the same bodies; only the paths and the query keys differ. The appointment
// values are the ones the workspace used before the patient page existed, and
// the tests pin them. Pure and import-free so `tsx --test` runs it.

export type QuickNoteSubject = { appointmentId: string } | { patientId: string };

function isAppointmentSubject(
  subject: QuickNoteSubject
): subject is { appointmentId: string } {
  return "appointmentId" in subject;
}

/** The subject's id, for a React key. */
export function quickNoteSubjectId(subject: QuickNoteSubject): string {
  return isAppointmentSubject(subject) ? subject.appointmentId : subject.patientId;
}

/** GET and PUT for the note. */
export function quickNotePath(subject: QuickNoteSubject): string {
  if (isAppointmentSubject(subject)) {
    return `/api/appointments/${encodeURIComponent(subject.appointmentId)}/patient-quick-note`;
  }

  return `/api/patients/${encodeURIComponent(subject.patientId)}/quick-note`;
}

/** GET for the note's copy in Healthie (NO-750). */
export function quickNoteHealthieSyncPath(subject: QuickNoteSubject): string {
  return `${quickNotePath(subject)}/healthie-sync`;
}

/** POST to send the note to Healthie again. */
export function quickNoteHealthieSyncRetryPath(subject: QuickNoteSubject): string {
  return `${quickNoteHealthieSyncPath(subject)}/retry`;
}

export function quickNoteQueryKey(subject: QuickNoteSubject): string[] {
  if (isAppointmentSubject(subject)) {
    return ["patient-quick-note", subject.appointmentId];
  }

  return ["patient-quick-note", "patient", subject.patientId];
}

/** Which kind of id a subject carries; with the id, a stable stand-in for it. */
export type QuickNoteSubjectScope = "appointment" | "patient";

export function quickNoteSubjectScope(
  subject: QuickNoteSubject
): QuickNoteSubjectScope {
  return isAppointmentSubject(subject) ? "appointment" : "patient";
}

export function quickNoteHealthieSyncQueryKey(subject: QuickNoteSubject): string[] {
  return quickNoteHealthieSyncQueryKeyFor(
    quickNoteSubjectScope(subject),
    quickNoteSubjectId(subject)
  );
}

/** The same key from the subject's scope and id, for callers that memoize on them. */
export function quickNoteHealthieSyncQueryKeyFor(
  scope: QuickNoteSubjectScope,
  id: string
): string[] {
  if (scope === "appointment") return ["patient-quick-note-healthie-sync", id];

  return ["patient-quick-note-healthie-sync", "patient", id];
}
